import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Monitor } from 'lucide-react';

const PrivacyPage = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy - URL Monitor Pro | Data Protection & Privacy</title>
        <meta name="description" content="Read URL Monitor Pro's Privacy Policy to understand how we collect, use, and protect your personal information and monitoring data." />
        <meta name="keywords" content="privacy policy, data protection, personal information, data security, GDPR, monitoring data" />
        <link rel="canonical" href="https://urlmonitor.pro/privacy" />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Navigation */}
        <nav className="bg-white shadow-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Link to="/" className="flex items-center">
                <Monitor className="w-8 h-8 text-blue-600 mr-3" />
                <span className="text-xl font-bold text-gray-900">URL Monitor Pro</span>
              </Link>
              <div className="hidden md:flex items-center space-x-8">
                <Link to="/#features" className="text-gray-600 hover:text-gray-900 transition-colors">Features</Link>
                <Link to="/pricing" className="text-gray-600 hover:text-gray-900 transition-colors">Pricing</Link>
                <Link to="/about" className="text-gray-600 hover:text-gray-900 transition-colors">About</Link>
                <Link to="/login" className="text-gray-600 hover:text-gray-900 transition-colors">Login</Link>
                <Link to="/register" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
                  Start Free
                </Link>
              </div>
            </div>
          </div>
        </nav>

        {/* Header */}
        <section className="bg-gray-50 py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
            <p className="text-gray-600">Last updated: December 2024</p>
          </div>
        </section>

        {/* Content */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="prose prose-lg max-w-none">

              <h2>1. Introduction</h2>
              <p>
                URL Monitor Pro ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website monitoring service.
              </p>

              <h2>2. Information We Collect</h2>
              
              <h3>2.1 Personal Information</h3>
              <p>We collect personal information that you voluntarily provide when you:</p>
              <ul>
                <li>Create an account (name, email address, password)</li>
                <li>Make a payment (billing information, payment details)</li>
                <li>Contact us for support (name, email, message content)</li>
                <li>Subscribe to our newsletter or marketing communications</li>
              </ul>

              <h3>2.2 Monitoring Data</h3>
              <p>When you use our monitoring service, we collect:</p>
              <ul>
                <li>URLs and domains you choose to monitor</li>
                <li>Response times and status codes from monitored websites</li>
                <li>Downtime incidents and related metadata</li>
                <li>Performance metrics and analytics data</li>
              </ul>

              <h3>2.3 Usage Information</h3>
              <p>We automatically collect certain information when you use our Service:</p>
              <ul>
                <li>IP address and geographic location</li>
                <li>Browser type and version</li>
                <li>Device information and operating system</li>
                <li>Pages visited and actions taken on our website</li>
                <li>Date and time of visits</li>
              </ul>

              <h2>3. How We Use Your Information</h2>
              <p>We use the collected information to:</p>
              <ul>
                <li>Provide and maintain our monitoring service</li>
                <li>Process payments and manage subscriptions</li>
                <li>Send monitoring alerts and notifications</li>
                <li>Provide customer support and respond to inquiries</li>
                <li>Improve our service and develop new features</li>
                <li>Send service-related communications</li>
                <li>Comply with legal obligations</li>
                <li>Prevent fraud and ensure service security</li>
              </ul>

              <h2>4. How We Share Your Information</h2>
              <p>We do not sell, trade, or rent your personal information. We may share your information in the following circumstances:</p>
              
              <h3>4.1 Service Providers</h3>
              <p>We may share information with trusted third-party service providers who help us operate our service:</p>
              <ul>
                <li>Payment processors (Stripe, Razorpay)</li>
                <li>Email service providers (Resend)</li>
                <li>Cloud hosting providers</li>
                <li>Analytics services</li>
              </ul>

              <h3>4.2 Legal Requirements</h3>
              <p>We may disclose your information if required by law or in response to:</p>
              <ul>
                <li>Court orders or legal processes</li>
                <li>Government investigations</li>
                <li>Requests from law enforcement agencies</li>
                <li>Protection of our rights and safety</li>
              </ul>

              <h2>5. Data Security</h2>
              <p>We implement appropriate security measures to protect your information:</p>
              <ul>
                <li>Encryption of data in transit and at rest</li>
                <li>Secure access controls and authentication</li>
                <li>Regular security audits and monitoring</li>
                <li>Secure payment processing</li>
                <li>Employee access restrictions and training</li>
              </ul>

              <h2>6. Data Retention</h2>
              <p>We retain your information for as long as necessary to:</p>
              <ul>
                <li>Provide our service to you</li>
                <li>Comply with legal obligations</li>
                <li>Resolve disputes and enforce agreements</li>
                <li>Maintain business records for operational purposes</li>
              </ul>
              <p>When you delete your account, we will delete your personal information within 30 days, except where we are required to retain it by law.</p>

              <h2>7. Your Rights</h2>
              <p>Depending on your location, you may have the following rights:</p>
              <ul>
                <li><strong>Access:</strong> Request access to your personal information</li>
                <li><strong>Correction:</strong> Request correction of inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                <li><strong>Portability:</strong> Request transfer of your data</li>
                <li><strong>Objection:</strong> Object to certain processing activities</li>
                <li><strong>Withdrawal:</strong> Withdraw consent where applicable</li>
              </ul>

              <h2>8. Cookies and Tracking</h2>
              <p>We use cookies and similar technologies to:</p>
              <ul>
                <li>Remember your preferences and settings</li>
                <li>Analyze website traffic and usage patterns</li>
                <li>Improve user experience</li>
                <li>Provide targeted content and advertisements</li>
              </ul>
              <p>You can control cookies through your browser settings, but some features may not work properly if cookies are disabled.</p>

              <h2>9. International Data Transfers</h2>
              <p>
                Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your information in accordance with applicable data protection laws.
              </p>

              <h2>10. Children's Privacy</h2>
              <p>
                Our service is not intended for children under 18 years of age. We do not knowingly collect personal information from children under 18. If we become aware that we have collected information from a child under 18, we will delete it promptly.
              </p>

              <h2>11. Updates to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of material changes by posting the updated policy on our website and updating the "Last updated" date. Your continued use of the service after any changes constitutes acceptance of the updated policy.
              </p>

              <h2>12. Contact Us</h2>
              <p>
                If you have questions about this Privacy Policy or want to exercise your rights, please contact us:
              </p>
              <p>
                Email: <a href="mailto:privacy@urlmonitor.pro" className="text-blue-600 hover:text-blue-700">privacy@urlmonitor.pro</a><br/>
                Address: URL Monitor Pro, Privacy Department
              </p>

              <h2>13. GDPR Compliance</h2>
              <p>
                For users in the European Union, we comply with the General Data Protection Regulation (GDPR). Our lawful basis for processing your information includes:
              </p>
              <ul>
                <li>Contract performance (providing our service)</li>
                <li>Legitimate interests (service improvement and security)</li>
                <li>Consent (marketing communications)</li>
                <li>Legal compliance (tax and regulatory requirements)</li>
              </ul>

              <h2>14. California Privacy Rights</h2>
              <p>
                California residents have additional rights under the California Consumer Privacy Act (CCPA), including the right to know what personal information we collect, use, and share, and the right to delete personal information.
              </p>

            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <Link to="/" className="flex items-center justify-center mb-4">
                <Monitor className="w-8 h-8 text-blue-400 mr-3" />
                <span className="text-xl font-bold">URL Monitor Pro</span>
              </Link>
              <p className="text-gray-400">
                © 2024 URL Monitor Pro. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default PrivacyPage;